import * as op from '../../common/errors/OperationResult'
import { GqlClientService } from '@/common/network/gql-client-service'
import { OperationResult } from '@/common/errors/OperationResult'
import type { CreateObjectInput, UpdateObjectInput } from '@/generated/graphql.ts'
import type { Dayjs } from 'dayjs'
import type { ObjectModel as _ObjectModel } from '@/generated/graphql.ts'
import type { ObjectQueryResultModel } from '@/objects/gql/ObjectQueryResultModel.ts'
import { log, logError } from '@/common/errors/log-utils.ts'
import type { ObjectModel, PropertyDefinitionModel } from '@/core/gql/gql-models-override.ts'
import type { PropertyValue } from '@/objects/gql/PropertyValue.ts'
import dayjs from 'dayjs'
import type { ObjectTypesService } from '@/object-types/services/ObjectTypesService.ts'

type ObjectsResponse = { objectsOfTypes: ObjectQueryResultModel }
type CreateObjectResponse = { createObject: _ObjectModel }

export class ObjectsApiService {
  constructor(
    private readonly gqlClientService: GqlClientService,
    private readonly typesService: ObjectTypesService,
  ) {}

  public async objectsOfTypes(
    typeIds: number[],
    abortSignal?: AbortSignal,
  ): Promise<OperationResult<Record<number, ObjectModel[]>>> {
    const query = `query($typeIds: [ID!]!) {
    objectsOfTypes(typeIds: $typeIds) {
      typeIdToObjects
    }
  }`
    const { result: body, error } = await this.gqlClientService.sendGqlRequest<ObjectsResponse>(
      query,
      {
        variables: { typeIds },
        abortSignal,
      },
    )

    if (error) {
      return op.error(error)
    }

    const raw = body!.data!.objectsOfTypes.typeIdToObjects
    const result: Record<number, ObjectModel[]> = {}
    for (const [typeIdStr, objects] of Object.entries(raw)) {
      const { models, failCount, firstError } = this.parseObjects(objects)
      if (failCount > 0) {
        logError(`discarded ${failCount} objects due to parse errors; first: ${firstError}`, {
          typeIdStr,
        })
      }
      result[Number(typeIdStr)] = models
    }
    return op.success(result)
  }

  public async create(input: CreateObjectInput): Promise<OperationResult<ObjectModel>> {
    // log('', { input })
    const query = `mutation($input: CreateObjectInput!) {
  createObject(input: $input) {
      id
      typeId
      propertyValues {
        definitionId
        value
      }
    }
  }`

    const { result: body, error } =
      await this.gqlClientService.sendGqlRequest<CreateObjectResponse>(query, {
        variables: { input },
      })

    if (error) {
      return op.error(error)
    }

    const { result: model, error: parseError } = this.toObjectModel(body!.data!.createObject)
    if (parseError) return op.error(parseError)
    return op.success(model!)
  }

  public async update(input: UpdateObjectInput): Promise<OperationResult<void>> {
    const query = `mutation($input: UpdateObjectInput!) {
    updateObject(input: $input)
    }`
    const { error } = await this.gqlClientService.sendGqlRequest(query, { variables: { input } })
    if (error) return op.error(error)
    return op.success(undefined)
  }

  public propertyValueToString(value: PropertyValue): string | null {
    if (value == null) return null
    if (typeof (value as Dayjs).isValid === 'function') return (value as Dayjs).toISOString()
    if (Array.isArray(value)) return JSON.stringify(value)
    return String(value)
  }

  private parseObjects(objects: _ObjectModel[]): {
    models: ObjectModel[]
    failCount: number
    firstError: string
  } {
    const models: ObjectModel[] = []
    let failCount = 0
    let firstError = ''
    for (const o of objects) {
      const { result: model, error: parseError } = this.toObjectModel(o)
      if (model != null) {
        models.push(model)
      } else {
        if (failCount === 0) firstError = parseError?.message ?? 'unknown'
        failCount++
      }
    }
    return { models, failCount, firstError }
  }

  private toObjectModel(raw: _ObjectModel): OperationResult<ObjectModel> {
    const propertyValues = new Map<number, PropertyValue>()
    for (const { definitionId, value } of raw.propertyValues) {
      const def = this.typesService.idToPropertyDef.get(definitionId)
      if (def == null) {
        console.error(`[ObjectsApiService] property definition not found for id ${definitionId}`)
        propertyValues.set(definitionId, value)
        continue
      }
      if (value == null) {
        propertyValues.set(definitionId, null)
        continue
      }
      const { result: parsed, error } = this.tryParsePropertyValue(value, def)
      if (error) return op.error(error)
      propertyValues.set(definitionId, parsed!)
    }
    return op.success({ ...raw, id: raw.id, propertyValues })
  }

  private tryParsePropertyValue(
    value: string,
    def: PropertyDefinitionModel,
  ): OperationResult<PropertyValue> {
    try {
      switch (def.type) {
        case 'number':
          return op.success(Number(value))
        case 'date':
          return op.success(dayjs(value))
        case 'toggle':
          return op.success(value === 'true')
        case 'select-1':
          return op.success(Number(value))
        case 'multi-select':
          return op.success(JSON.parse(value) as number[])
        case 'text':
        default:
          return op.success(value)
      }
    } catch {
      return op.error(`failed to parse property value for type ${def.type}: ${value}`)
    }
  }
}
